import axios from "axios";
import { API_KEY } from "../config/appConfig";
import { Toaster } from "../shared/components/common/toaster";
import { CultureHelper } from "../helpers/cultureHelper";
import { getRefreshToken, getToken, refreshSession } from "./authService";
import { signOut } from "../auth/authUtilities";
import { buildLocalizeRoute } from "../helpers/languageHelper";
import { AppRoutes } from "../constants/appRoutes";

const redirectToLogin = () => {
  signOut();
  window.location.href = buildLocalizeRoute(AppRoutes.Login);
};

const axiosNew = axios.create({
  baseURL: `${API_KEY}/api/`,
  headers: {
    "Content-Type": "application/json",
  },
  // Treat only 2xx as success; all other statuses are errors that
  // will be handled by the response interceptor on a per-request basis.
  validateStatus: function (status) {
    return status >= 200 && status < 300;
  },
});

axiosNew.interceptors.request.use(function (config) {
  config.params = {
    ...config.params,
    ...{ culture: CultureHelper.currentCulture() },
  };
  const token = getToken();
  config.headers.Authorization = token ? `Bearer ${token}` : "";
  return config;
});

axiosNew.interceptors.response.use(
  (res) => {
    if (
      res.status >= 200 &&
      res.status < 300 &&
      res.config.method !== "get" &&
      !res.config?.headers?.hide_success_toaster
    ) {
      // Toaster({ msg: 'Request Successfully Completed', isError: false });
    }
    return res;
  },
  async (error) => {
    const originalConfig = error.config;
    if (
      originalConfig?.url !== buildLocalizeRoute(AppRoutes.Login) &&
      error.response
    ) {
      if (error.response.status === 401) {
        if (!originalConfig._retriedAfterRefresh && getRefreshToken()) {
          originalConfig._retriedAfterRefresh = true;
          const accessToken = await refreshSession();
          if (accessToken) {
            originalConfig.headers.Authorization = `Bearer ${accessToken}`;
            return axiosNew(originalConfig);
          }
        }

        Toaster({ msg: "Session expired, Please login!" });
        redirectToLogin();
        return Promise.reject(error);
      }
    }
    if (
      !error?.config?.headers?.hide_toaster &&
      !error?.response?.data?.UserLockedOut &&
      !error?.response?.data?.loginEmailNotFound &&
      !error?.response?.data?.NoAccountFoundForGoogleEmail &&
      !error?.response?.data?.GoogleEmailNotVerified &&
      !error?.response?.data?.GoogleTokenInvalidCode &&
      !error?.response?.data?.GoogleSignInDisabledCode &&
      !error?.response?.data?.NoAccountFoundForAppleEmail &&
      !error?.response?.data?.AppleEmailNotVerified &&
      !error?.response?.data?.AppleTokenInvalidCode &&
      !error?.response?.data?.AppleSignInDisabledCode &&
      !error?.response?.data?.AppleEmailNotSharedCode &&
      !error?.response?.data?.AppleEmailHiddenCode
    ) {
      if (axios.isCancel(error)) {
        return;
      }

      if (error.response?.status === 403) {
        Toaster({
          msg: formatError(error?.response?.data) || "Access Denied!",
        });
      } else {
        if (
          error?.response?.data?.IbanIsNullOrEmpty ||
          error?.response?.data?.BankNameIsNullOrEmpty ||
          error?.response?.data?.insufficientFunds
        ) {
        } else {
          Toaster({
            msg:
              formatError(error?.response?.data) ||
              "Something went wrong. Please try again",
          });
        }
      }
    }
    return Promise.reject(error);
  }
);

export const axiosGet = (params, url, id, responseType = "", headers) => {
  let fullUrl = id ? `${url}/${id}` : url;
  return axiosNew.get(fullUrl, {
    params: params,
    headers: headers,
    responseType: responseType,
  });
};

export const axiosPost = (body, url, headers, responseType) => {
  return axiosNew.post(url, body, {
    headers: headers,
    responseType: responseType,
  });
};

export const axiosPut = (body, url, headers, responseType) => {
  return axiosNew.put(url, body, {
    headers: headers,
    responseType: responseType,
  });
};

export const axiosPatch = (
  url,
  body,
  headers,
  responseType,
  onUploadProgress
) => {
  return axiosNew.patch(url, body, {
    headers: headers,
    responseType: responseType,
    onUploadProgress: onUploadProgress,
  });
};

export const axiosDelete = (url, headers, responseType) => {
  return axiosNew.delete(url, {
    headers: headers,
    responseType: responseType,
  });
};

const formatErrorObject = (error) => {
  const keys = Object.keys(error);
  const errors = keys.map((k) => error[k]);
  if (errors.join("<br />") !== "[object Object]") {
    return errors.join("<br />");
  }
  if (errors.length && error[0]?.message) {
    return error[0]?.message;
  }
  return null;
};

export const formatError = (error) => {
  if (!error) {
    return "Something went wrong. Please try again";
  }

  if (typeof error === "string") {
    let parseValues = JSON.parse(error);
    if (Object.keys(parseValues).length) {
      return formatErrorObject(parseValues);
    }
    return error;
  }

  if (error?.errors && typeof error?.errors == "object") {
    return formatErrorObject(error?.errors);
  }

  if (typeof error == "object") {
    return formatErrorObject(error);
  }

  return error;
};

export { axiosNew };
